import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { getCard } from "./guestApi";
import { saveGuest } from "./guestSession";
import { loadPlace } from "./placeSession";
import PendingPoints from "./PendingPoints";
import AtCounterButton from "./AtCounterButton";
import ScanToOrder from "./ScanToOrder";
import { alreadyAlerted, fire as firePager } from "./readyAlert";
import useVenueManifest from "./useVenueManifest";
import useInstallPrompt from "./useInstallPrompt";
import "./scan-stay.css";

/**
 * The customer card at /c/{token} — the guest's persistent home.
 *
 * Bookmarkable and cross-device because the signed token in the URL IS the
 * capability; there is no cookie, login or app. VIEW-ONLY by construction:
 * redemption happens at the till, so this page shows what the guest has and what
 * the counter will honour, and never offers a button that spends.
 *
 * It polls, which is the point of P4 3c — points appear here within seconds of
 * the cashier tapping "Mark paid", and a ticket still in the kitchen shows its
 * own progress without the guest holding the order screen open.
 *
 * ⚠️ P5d — AND IT CANNOT ORDER. Being bookmarkable is what made this page useful
 * and also what made it dangerous: opened from home it happily linked through to
 * a live order screen. It is now BROWSE-ONLY unless a place token is live in
 * this tab — points, history, rewards and the "I'm at the counter" doorbell all
 * stay, the order link becomes "scan the QR on your table or the screen".
 *
 * Note what did NOT change: the doorbell still writes (it spends nothing and
 * only puts a name on the till board), and redemption still lives at the till.
 * The card was already view-only for POINTS; P5d makes it view-only for the
 * KITCHEN too.
 */

const POLL_MS = 8000;
const REWARD_ICON = { free_item: "☕", discount: "🏷️", free_spin: "🎡", default: "🎁" };

// Same map as the tracker, and for the same reason: an unknown status must not
// silently render as the first one. open_orders only carries live tickets today,
// but that is a query detail, not a guarantee this component should rely on.
const STATUS_KEY = {
  new: "queued",
  preparing: "preparing",
  ready: "ready",
  picked_up: "collected",
  cancelled: "cancelled",
};

export default function CustomerCard() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const isAr = (i18n.resolvedLanguage || i18n.language || "en").startsWith("ar");

  const [card, setCard] = useState(null);
  const [status, setStatus] = useState("loading"); // loading|ready|gone|error
  const alive = useRef(true);

  const localName = useCallback((o) => (isAr && o?.name_ar ? o.name_ar : o?.name), [isAr]);

  const load = useCallback(async () => {
    try {
      const data = await getCard(token);
      if (!alive.current) return;
      setCard(data);
      setStatus("ready");
      // Refresh the convenience cache so returning to /v/{venue} still knows
      // them — and so a card opened on a second device teaches that device too.
      saveGuest(data?.venue?.code, {
        token,
        customerId: data?.customer?.id,
        name: data?.customer?.name,
      });
    } catch (e) {
      if (!alive.current) return;
      // 404 = rotated, revoked or simply wrong. That is a real answer, not an
      // outage: the link is dead and the guest should re-identify at the venue.
      setStatus(e?.response?.status === 404 ? "gone" : "error");
    }
  }, [token]);

  useEffect(() => {
    alive.current = true;
    load();
    const id = setInterval(load, POLL_MS);
    return () => {
      alive.current = false;
      clearInterval(id);
    };
  }, [load]);

  /* ── the pager, on the card too ─────────────────────────────────────────── */
  //
  // The card is where a guest ends up between ordering and collecting — it is
  // linked from the tracker and it is the thing they bookmarked — so a ticket
  // that goes ready while they are looking at their points has to buzz here as
  // well. Same guard as the tracker (localStorage, per order id), so having both
  // open rings exactly once.
  useEffect(() => {
    (card?.open_orders || []).forEach((o) => {
      if (o.status !== "ready") return;
      firePager({
        orderId: o.id,
        title: t("scan_stay.order.ready_notif_title", { no: o.no }),
        body: o.table_number
          ? t("scan_stay.order.ready_table", { table: o.table_number })
          : t("scan_stay.order.ready_counter"),
      });
    });
  }, [card, t]);

  // Per-venue add-to-home naming, once we know which venue this card belongs to.
  useVenueManifest(card?.venue?.name, card?.venue?.code, `/c/${token}`);
  const { mode: installMode, promptInstall } = useInstallPrompt();
  const [showIosHelp, setShowIosHelp] = useState(false);

  const toggleLang = () => i18n.changeLanguage(isAr ? "en" : "ar");

  const balance = card?.points?.balance ?? 0;
  const next = card?.next_reward;
  const pct = useMemo(
    () => (next && next.cost > 0 ? Math.min(100, Math.round((balance / next.cost) * 100)) : 100),
    [next, balance]
  );

  if (status === "loading") {
    return (
      <div className="ss-root" dir={isAr ? "rtl" : "ltr"}>
        <div className="ss-center"><div className="ss-spin" /><div>{t("scan_stay.loading")}</div></div>
      </div>
    );
  }

  if (status !== "ready") {
    const gone = status === "gone";
    return (
      <div className="ss-root" dir={isAr ? "rtl" : "ltr"}>
        <div className="ss-center">
          <div style={{ fontSize: 40 }}>{gone ? "🔑" : "📡"}</div>
          <div style={{ fontWeight: 700, color: "var(--ss-ink)" }}>
            {t(gone ? "card.gone_title" : "scan_stay.connection_error")}
          </div>
          <div>{t(gone ? "card.gone_sub" : "scan_stay.connection_error_sub")}</div>
          {!gone && (
            <button type="button" className="ss-cta" style={{ maxWidth: 220, marginTop: 6 }} onClick={load}>
              {t("scan_stay.retry")}
            </button>
          )}
        </div>
      </div>
    );
  }

  const c = card.customer;
  const tier = card.tier;
  const open = card.open_orders || [];
  // The cheapest reward they can actually walk out with — what the doorbell is
  // for, and what it names. `reachable` is the server's own affordability call,
  // so this can never offer something the till would refuse.
  const affordable = (card.rewards || []).find((r) => r.reachable) || null;

  return (
    <div className="ss-root" dir={isAr ? "rtl" : "ltr"}>
      <div className="ss-card cardpage">
        {/* espresso header band */}
        <div className="cc-band">
          <div className="cc-band-top">
            <span className="cc-venue">{card.venue?.name}</span>
            <button type="button" className="ss-lang dark" onClick={toggleLang} aria-label="Toggle language">
              {isAr ? "EN" : "عربي"}
            </button>
          </div>
          <div className="cc-name">{c.name || t("card.guest")}</div>
          <div className="cc-meta">
            {c.member_since && <span>{t("card.member_since", { date: c.member_since })}</span>}
            <span className="cc-dot" aria-hidden="true">•</span>
            <span>{t("card.visits", { n: c.visit_count })}</span>
          </div>

          <div className="cc-balance">
            <span className="cc-balance-n">{balance}</span>
            <span className="cc-balance-l">{t("scan_stay.your_points")}</span>
          </div>

          <PendingPoints points={card.points?.pending} dark />

          {/* ⚠️ P5d — THIS LINK IS WHERE THE BUG CAME FROM. It used to offer
              "Order" unconditionally, and the card is bookmarkable by design, so
              a guest at home tapped it and sent real tickets to a kitchen with
              nobody in the building.

              It now shows the way back to ordering ONLY while a place token is
              live in this tab — i.e. the guest reached the card from a QR they
              scanned minutes ago and is demonstrably still here. Opened cold
              from a bookmark or a home-screen tile, it becomes the scan prompt:
              the card keeps everything it was ever for (points, history,
              rewards, the counter doorbell) and loses only the one action that
              needs a body in the room.

              Read on every render rather than held in state — sessionStorage is
              the authority for "this tab", the card polls anyway, and a stale
              copy here is precisely the class of bug being closed. */}
          {card.venue?.code &&
            (loadPlace(card.venue.code) ? (
              // A plain link, not a router push, so it works identically from an
              // installed PWA tile. The session is already cached (including the
              // customer id), so the menu recognises them on arrival.
              <a className="cc-order-cta" href={`/v/${encodeURIComponent(card.venue.code)}`}>
                {t("card.order_cta")}
              </a>
            ) : (
              <ScanToOrder compact />
            ))}
        </div>

        {/* live tickets — the tracker */}
        {open.length > 0 && (
          <section className="ss-block">
            <h2 className="ss-block-title">{t("card.your_order")}</h2>
            {open.map((o) => (
              <div key={o.id} className={`cc-ticket ${o.status}${o.status === "ready" ? " paged" : ""}`}>
                {/* A ready ticket on the card gets the same loud treatment as
                    the tracker — the guest may have been buzzed while on this
                    page, and "why did my phone just do that" has to be
                    answerable at a glance. */}
                {o.status === "ready" && alreadyAlerted(o.id) && (
                  <div className="ss-paged" role="alert">
                    <span className="ic" aria-hidden="true">🔔</span>
                    <span className="tx">{t("scan_stay.order.ready_bang")}</span>
                  </div>
                )}
                <div className="cc-ticket-head">
                  <span className="cc-ticket-no mono">#{o.no}</span>
                  <span className={`cc-ticket-status ${o.status}`}>
                    {t(`card.status.${STATUS_KEY[o.status] || "queued"}`)}
                  </span>
                </div>
                <div className="cc-ticket-item">
                  {o.qty} × {localName({ name: o.item_name, name_ar: o.item_name_ar })}
                  {o.table_number && <> · {t("scan_stay.at_table", { table: o.table_number })}</>}
                </div>
                <div className="cc-steps" aria-hidden="true">
                  {["new", "preparing", "ready"].map((s, i) => (
                    <span key={s} className={`cc-step${["new", "preparing", "ready"].indexOf(o.status) >= i ? " on" : ""}`} />
                  ))}
                </div>
                <div className="cc-ticket-pay">
                  {o.paid ? t("card.paid_done") : t("card.pay_at_counter")}
                </div>
              </div>
            ))}
          </section>
        )}

        {/* progress to the next reward */}
        <section className="ss-block">
          <div className="ss-points-lab">
            <span>{t("scan_stay.your_points")}</span>
            <span className="n">{next ? `${balance}/${next.cost}` : balance}</span>
          </div>
          <div className="ss-track"><div className="ss-fill" style={{ width: `${pct}%` }} /></div>
          {next ? (
            <div className="ss-hint">{t("scan_stay.to_next", { pts: next.remaining, reward: localName(next) })}</div>
          ) : (
            (card.rewards || []).length > 0 && <div className="ss-hint">{t("scan_stay.all_unlocked")}</div>
          )}

          <div className={`cc-redeem ${c.can_redeem ? "on" : "off"}`}>
            {c.can_redeem ? t("card.redeem_at_till") : t("card.redeem_locked")}
          </div>

          {/* The doorbell. Only when something is genuinely in reach — a
              request the counter would turn away is worse than none. */}
          {c.can_redeem && affordable && (
            <AtCounterButton
              token={token}
              reward={{ id: affordable.id, label: localName(affordable) }}
              atCounter={card.at_counter}
              onRaised={load}
            />
          )}
        </section>

        {/* tier, from the venue's own milestone ladder */}
        {tier && (tier.reached || tier.next) && (
          <section className="ss-block">
            <h2 className="ss-block-title">{t("card.tier_title")}</h2>
            <div className="cc-tier">
              <div className="cc-tier-now">
                {tier.reached
                  ? t("card.tier_reached", { visits: tier.reached.visits })
                  : t("card.tier_none")}
              </div>
              {tier.next && (
                <div className="cc-tier-next">
                  {t("card.tier_next", { n: tier.next.remaining, bonus: tier.next.bonus })}
                </div>
              )}
            </div>
          </section>
        )}

        {/* the reward ladder — restored here from the old guest page */}
        {(card.rewards || []).length > 0 && (
          <section className="ss-block">
            <h2 className="ss-block-title">{t("card.rewards_title")}</h2>
            <div className="ss-rewards">
              {card.rewards.map((r) => (
                <div key={r.id} className={`ss-reward${r.reachable ? " reachable" : ""}`}>
                  <span className="ic">{REWARD_ICON[r.type] || REWARD_ICON.default}</span>
                  <span className="tx">
                    {localName(r)}
                    {r.reachable && <small>{t("scan_stay.ready_to_redeem")}</small>}
                  </span>
                  <span className="cost">{r.points_required}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* recent movements */}
        {(card.activity || []).length > 0 && (
          <section className="ss-block">
            <h2 className="ss-block-title">{t("card.activity_title")}</h2>
            <ul className="cc-activity">
              {card.activity.map((a, i) => (
                <li key={i}>
                  <span className={`cc-act-pts ${a.points >= 0 ? "up" : "down"}`}>
                    {a.points >= 0 ? `+${a.points}` : a.points}
                  </span>
                  <span className="cc-act-src">{t(`card.source.${a.source}`, { defaultValue: a.source })}</span>
                  <span className="cc-act-bal mono">{a.balance_after}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* A real install, not a sentence telling the guest to go find the
            browser menu. Chromium gets the captured prompt; iOS has no
            programmatic install at all, so it gets the honest manual route;
            an already-installed card offers nothing. */}
        {installMode !== "installed" && (
          <div className="cc-install">
            {installMode === "prompt" && (
              <button type="button" className="cc-install-btn" onClick={promptInstall}>
                {t("card.install_cta")}
              </button>
            )}
            {installMode === "manual" && (
              <button type="button" className="cc-install-btn" onClick={() => setShowIosHelp((v) => !v)}>
                {t("card.install_cta")}
              </button>
            )}
            {installMode === "manual" && showIosHelp && (
              <p className="cc-install-help">{t("card.install_ios")}</p>
            )}
            {installMode === "none" && <p className="ss-paynote">{t("card.bookmark_hint")}</p>}
          </div>
        )}
      </div>

      <div className="ss-foot">{t("scan_stay.powered_by")}</div>
    </div>
  );
}
